import React, { useState, useEffect, Fragment, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useParams, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { Tab, Menu, Transition } from "@headlessui/react"
import { HiChevronDown } from "react-icons/hi"

import Header from "../../components/header"
import ConfirmationModal from "../../components/confirmation"
import { twClassNames } from "../../helpers/classNames"
import { fetchAllDestinations } from "../../actions/destinations"
import { fetchActivities } from "../../actions/activities"
import { TabActivities, TabHandbooks } from "./components"
import type { RootState } from "../../store"

export type ConfirmationState = {
  show: boolean
  title: string
  content: string
  danger?: boolean
  callback: (() => void) | null
}

type DropdownButtonProps = {
  title: string
  children: React.ReactNode
}

const DropdownButton: React.FC<DropdownButtonProps> = ({ title, children }) => (
  <Menu as="div" className="tw-relative tw-inline-block tw-text-left">
    <Menu.Button className="tw-btn tw-btn-primary tw-min-w-[180px]">
      {title}
      <HiChevronDown className="tw-absolute tw-right-0 tw-mr-4 tw-text-white" aria-hidden="true" />
    </Menu.Button>
    <Transition
      as={Fragment}
      enter="tw-transition tw-ease-out tw-duration-100"
      enterFrom="tw-transform tw-opacity-0 tw-scale-95"
      enterTo="tw-transform tw-opacity-100 tw-scale-100"
      leave="tw-transition tw-ease-in tw-duration-75"
      leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
      leaveTo="tw-transform tw-opacity-0 tw-scale-95">
      <Menu.Items
        className="tw-absolute tw-right-0 tw-z-10 tw-mt-2 tw-w-56 tw-origin-top-right tw-rounded-md tw-bg-white
          tw-shadow-lg tw-border tw-border-dark-15 focus:tw-outline-none">
        <div className="tw-py-1">{children}</div>
      </Menu.Items>
    </Transition>
  </Menu>
)

type RouteParams = {
  active_tab?: "activities" | "handbooks"
}

const CONFIRMATION_DEFAULT = {
  show: false,
  callback: null,
  danger: false,
  title: "",
  content: ""
}

const ProjectsContainer: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const destinations = useSelector((state: RootState) => state.destinations.destinations)
  const activities = useSelector((state: RootState) => state.activities.activities)
  const isLoading = useSelector((state: RootState) => state.destinations.isLoading || state.activities.isLoading)

  const [confirmation, setConfirmation] = useState<ConfirmationState>(CONFIRMATION_DEFAULT)
  const [query, setQuery] = useState("")
  const [maxRows, setMaxRows] = useState(5)

  const { active_tab } = useParams<RouteParams>()

  const filteredDestinations = useMemo(() => {
    if (!query) return destinations

    const q = query.toLowerCase()
    return destinations?.filter(item => item.name.toLowerCase().includes(q))
  }, [query, destinations])

  const filteredActivities = useMemo(() => {
    if (!query) return activities

    const q = query.toLowerCase()
    return activities?.filter(item => item.name.toLowerCase().includes(q))
  }, [query, activities])

  const fetchContent = useCallback(() => {
    try {
      dispatch(fetchAllDestinations())
      dispatch(fetchActivities())
    } catch {
      // handle error
    }
  }, [dispatch])

  useEffect(() => {
    fetchContent() // Fetch activities and destinations
    // Get user preferences
    const _maxRows = Number(localStorage.getItem("display.max_rows"))
    if (_maxRows) {
      setMaxRows(_maxRows)
    }
  }, [fetchContent])

  useEffect(() => {
    localStorage.setItem("display.max_rows", maxRows.toString())
  }, [maxRows])

  return (
    <>
      <ConfirmationModal
        show={confirmation.show}
        title={confirmation.title}
        content={confirmation.content}
        danger={confirmation.danger}
        onOK={confirmation.callback}
        onCancel={() => setConfirmation(CONFIRMATION_DEFAULT)}
      />
      <Header />
      <div className="xl:tw-container tw-mx-auto tw-w-full tw-mt-24 tw-px-4 sm:tw-px-12">
        <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("projects.title")}</h1>
        <div className="tw-mb-12 tw-w-full">
          <Tab.Group
            selectedIndex={active_tab === "activities" ? 1 : 0}
            onChange={index => history.push(index === 0 ? "/handbooks" : "/activities")}>
            <div className="tw-shadow tw-bg-white tw-rounded">
              {/* Header group */}
              <div className="tw-flex tw-p-6 tw-pb-2 tw-flex-col-reverse md:tw-flex-row">
                {/* Search bar */}
                <div className="tw-grow md:tw-mr-6 max-md:tw-mt-4">
                  <div
                    className="tw-relative tw-w-full tw-rounded-lg tw-bg-white tw-text-left tw-cursor-default tw-h-10
                      tw-border tw-border-dark-15 focus:tw-outline-none">
                    <input
                      id="search"
                      type="text"
                      onChange={event => setQuery(event.target.value)}
                      className="tw-w-full tw-border-none tw-py-2 tw-pl-3 tw-pr-10 tw-leading-5 tw-rounded-lg
                        sm:tw-text-sm focus:tw-outline-primary-50"
                      placeholder={t("common.search.placeholder")}
                      spellCheck="false"
                    />
                  </div>
                </div>
                {/* Button group */}
                <div className="tw-flex tw-justify-center md:tw-justify-end tw-content-end tw-space-x-4">
                  <button
                    className="tw-btn tw-btn-outline tw-min-w-[180px]"
                    disabled={isLoading}
                    onClick={fetchContent}>
                    <span>{t("projects.button.refresh")}</span>
                  </button>
                  <DropdownButton title={t("projects.button.create.title")}>
                    <Menu.Item>
                      <Link
                        to="/handbook/create"
                        className="tw-block tw-px-4 tw-py-2 hover:tw-bg-gray hover:tw-no-underline hover:tw-text-dark">
                        <span>{t("projects.button.create.handbook")}</span>
                      </Link>
                    </Menu.Item>
                    <Menu.Item>
                      <Link
                        to="/activities/create"
                        className="tw-block tw-px-4 tw-py-2 hover:tw-bg-gray hover:tw-no-underline hover:tw-text-dark">
                        <span>{t("projects.button.create.activity")}</span>
                      </Link>
                    </Menu.Item>
                  </DropdownButton>
                </div>
              </div>
              {/* Tabs */}
              <Tab.List className="tw-border-b-2 tw-border-gray tw-mb-6">
                <ul className="tw-flex tw-flex-wrap -tw-mb-px tw-mx-6 tw-space-x-2">
                  <Tab as={Fragment}>
                    {({ selected }) => (
                      <li
                        className={twClassNames(
                          "tw-inline-block tw-text-dark-50 tw-cursor-pointer tw-py-4 tw-px-12 tw-text-center",
                          "hover:tw-text-dark-75 hover:tw-border-dark-25 tw-font-semibold",
                          "tw-border-transparent tw-border-b-2 focus:tw-outline-none",
                          selected && "!tw-text-dark !tw-border-primary"
                        )}>
                        {t("projects.handbooks", { length: filteredDestinations?.length ?? 0 })}
                      </li>
                    )}
                  </Tab>
                  <Tab as={Fragment}>
                    {({ selected }) => (
                      <li
                        className={twClassNames(
                          "tw-inline-block tw-text-dark-50 tw-cursor-pointer tw-py-4 tw-px-12 tw-text-center",
                          "hover:tw-text-dark-75 hover:tw-border-dark-25 tw-font-semibold",
                          "tw-border-transparent tw-border-b-2 focus:tw-outline-none",
                          selected && "!tw-text-dark !tw-border-primary"
                        )}>
                        {t("projects.activities", { length: filteredActivities?.length ?? 0 })}
                      </li>
                    )}
                  </Tab>
                </ul>
              </Tab.List>
              {/* Content */}
              <div className="tw-px-6">
                <Tab.Panels className="tw-w-full tw-min-h-24">
                  {/* Handbooks */}
                  <TabHandbooks
                    destinations={filteredDestinations ?? []}
                    query={query}
                    maxRows={maxRows}
                    onMaxRowSelect={setMaxRows}
                    setConfirmation={setConfirmation}
                    hideConfirmation={() => setConfirmation(CONFIRMATION_DEFAULT)}
                    isLoading={isLoading}
                  />
                  {/* Activities */}
                  <TabActivities
                    activities={filteredActivities ?? []}
                    query={query}
                    maxRows={maxRows}
                    onMaxRowSelect={setMaxRows}
                    setConfirmation={setConfirmation} // TODO: Replace with common toast
                    hideConfirmation={() => setConfirmation(CONFIRMATION_DEFAULT)} // TODO: Replace with common toast
                    isLoading={isLoading}
                  />
                </Tab.Panels>
              </div>
            </div>
          </Tab.Group>
        </div>
      </div>
    </>
  )
}

export default ProjectsContainer
